/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import classNames from "classnames";
import { useLoadScript } from "@react-google-maps/api";
import { FC, KeyboardEvent, useEffect, useState } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { useTranslation } from "react-i18next";
import { Input } from "..";
import { getEnvs } from "state/EnvironmentsContext/EnvContext";

type Props = {
  onSelect: Function;
  className?: string;
  value: string;
  onChange?: Function;
};

const LIBRARIES: any = ["places"];

const AutoCompletePlaces: FC<Props> = ({
  onSelect,
  className,
  value,
  onChange,
  ...inputProps
}) => {
  const { t } = useTranslation();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: getEnvs((state) => state.google_map_api) || "",
    libraries: LIBRARIES,
  });

  const [address, setAddress] = useState("");
  useEffect(() => {
    if (value !== address && value) setAddress(value);
  }, [value]);
  const handleChange = (address: string) => {
    setAddress(address);
    onChange?.(address);
    // to clear the address when user deletes the input
    if (!address) onSelect?.(address);
  };
  const handleOnSelect = (address: string) => {
    setAddress(address);
    onSelect?.(address);
  };
  if (!isLoaded) return null;
  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={(address) => handleOnSelect(address)}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div className="relative z-[1]">
          <Input
            {...getInputProps({
              placeholder: t("forms.placeholders.searchByAddress"),
            })}
            onKeyDown={(e: KeyboardEvent): void => {
              if (e.key === "Enter") {
                handleOnSelect(address);
              }
            }}
            {...inputProps}
          />
          <div
            className={classNames("text-gray-900 absolute inset-x-0 w-full", {
              "border-2": suggestions.length > 0,
            })}
          >
            {suggestions.map((suggestion, i) => {
              const style = suggestion.active
                ? {
                    backgroundColor: "var(--primary-800)",
                    color: "white",
                    cursor: "pointer",
                  }
                : {
                    backgroundColor: "var(--primary)",
                    cursor: "pointer",
                    color: "white",
                  };
              return (
                <div
                  {...getSuggestionItemProps(suggestion, {
                    style,
                  })}
                  key={i}
                  className="p-2"
                >
                  <span>{suggestion.description}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </PlacesAutocomplete>
  );
};

export const getLatLngByAddress = async (address: string) => {
  try {
    if (!address) {
      return {
        lat: "",
        lng: "",
      };
    }
    return await geocodeByAddress(address).then(async (result) => {
      const latLng = await getLatLng(result[0]);
      return {
        lat: latLng.lat,
        lng: latLng.lng,
      };
    });
  } catch (err) {
    return {
      lat: "",
      lng: "",
    };
  }
};

export default AutoCompletePlaces;
